import React, { useState } from "react";
import { Box, Grid, Stack } from "@mui/material";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";
import { DynamicModal } from "src/components/base/components/DynamicModal";
import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { toast } from "react-toastify";
import { findApiErrorMessage } from "src/components/reusable/utils/utils";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { OtherReason } from "src/components/base/components/FormComponents/FormComponents";
import Reasons from "../Subscriptions/ReasonWidget";
import { createState } from "src/utils/create_zustand_store";
import { TSubscriptionDetails } from "src/shared/types/globalStotre.types";
import { SSButton } from "src/components/base/components/SSButton";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";

export interface CancelSubscriptionModalState {
  isOpen: boolean;
  passId: number | null;
}

export interface UseCancelSubscriptionModalReturn {
  isOpen: boolean;
  passId: number;
  set: (state: Partial<CancelSubscriptionModalState>) => void;
  reset: () => void;
}

export const useCancelSubscriptionModal =
  createState<UseCancelSubscriptionModalReturn>({
    isOpen: false,
    passId: 0,
  } as UseCancelSubscriptionModalReturn);

export function CancelSubscriptionModal() {
  const { md: mdUp } = useCssMediaQuery("up");
  const cancelSubscriptionModal = useCancelSubscriptionModal();
  const { passId } = cancelSubscriptionModal;
  const { setGlobalState, subscriptionDetails } = useGlobalStore();
  const [isConfirming, setIsConfirming] = useState(false);
  const [step, setStep] = useState(1);
  const methods = useForm();
  const { watch, handleSubmit, reset } = methods;
  const selectedReason = watch("reason");
  const {
    addCancellationDetailsApi,
    updateSubscriptionDetailsApi,
    getSubscriptionDetailsApi,
  } = useApiRoutes();

  const onClose = () => {
    cancelSubscriptionModal.reset();
    reset();
    setStep(1);
  };

  const handleNextStep = () => {
    setStep(2);
  };

  const handleCancellation: SubmitHandler<Record<string, any>> = async (
    data
  ) => {
    setIsConfirming(true);
    try {
      const subDetail = subscriptionDetails?.find((itm) => {
        return itm.passId === passId;
      }) as TSubscriptionDetails;
      await addCancellationDetailsApi.post({
        passId,
        reason: data.reason,
        feedback: data.otherReason || "",
      });
      await updateSubscriptionDetailsApi.post({
        passId,
        seatCount: subDetail?.seatCount,
        subscriptionStatus: "INACTIVE",
      });

      handleGetSubscriptionDetails();
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }

    setIsConfirming(false);
    onClose();
  };

  const handleGetSubscriptionDetails = async () => {
    try {
      const subscriptionDetails = await getSubscriptionDetailsApi.get();
      setGlobalState({ subscriptionDetails });
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <FormProvider {...methods}>
      <DynamicModal
        isOpen={cancelSubscriptionModal.isOpen}
        hideTitleIcon
        onClose={onClose}
        title="We’re sorry to see you go!"
        renderBody={() => {
          return (
            <Box>
              <form onSubmit={handleSubmit(handleCancellation)}>
                {step === 1 ? (
                  <Box p={3}>
                    Your feedback is important to us. Please let us know why you
                    are cancelling.
                  </Box>
                ) : (
                  <Box px={3} py={2}>
                    <Reasons name="reason" required />
                    {selectedReason === "other" && (
                      <OtherReason
                        name="otherReason"
                        required
                        disabled={selectedReason !== "other"}
                      />
                    )}
                  </Box>
                )}
              </form>
            </Box>
          );
        }}
        renderFooter={() => {
          return (
            <Stack
              direction={"row"}
              spacing={2}
              p={2}
              justifyContent={"flex-end"}
            >
              <SSButton
                size={mdUp ? "large" : "small"}
                onClick={onClose}
                variant="outlined"
                fullWidth={mdUp ? false : true}
                sx={{ lineHeight: 1.2, py: mdUp ? 2 : 1 }}
              >
                Keep My Subscription
              </SSButton>

              <SSButton
                size={mdUp ? "large" : "small"}
                fullWidth={mdUp ? false : true}
                loading={isConfirming}
                disabled={
                  isConfirming ||
                  (step === 2 &&
                    selectedReason === "other" &&
                    !methods.watch("otherReason"))
                }
                onClick={
                  step === 1 ? handleNextStep : handleSubmit(handleCancellation)
                }
                sx={{
                  py: mdUp ? 2 : 1,
                  lineHeight: 1.2,
                }}
              >
                {step === 1 ? "Continue" : "Complete Cancellation"}
              </SSButton>
            </Stack>
          );
        }}
        slot={{
          dialog: {
            style: {
              padding: "0px 12px 0px 12px",
            },
          },
        }}
      />
    </FormProvider>
  );
}
